import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { SpinnerGap, FacebookLogo, Upload, Warning, CheckCircle, Eye } from '@phosphor-icons/react';

/**
 * Tab "Importar Leads Facebook" — recovery quando o webhook legacy não
 * está a entregar leads (App restringida, token expirado, etc.).
 *
 * Workflow:
 *  1. Admin escolhe o formulário Facebook (dropdown alimentado pela DB).
 *  2. Faz upload do export CSV/TSV/XLSX do Meta Ads Manager.
 *  3. (opcional) Dry-run para ver preview sem escrever na DB.
 *  4. Confirma → backend invoca `sp_createLead` (que distribui ao consultor)
 *     e envia email "Atribuição de uma Nova Lead" se a checkbox estiver activa.
 */
export default function TabFacebookLeads() {
  const [forms, setForms] = useState([]);
  const [formId, setFormId] = useState('');
  const [file, setFile] = useState(null);
  const [sendEmails, setSendEmails] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/backoffice/leads/facebook-forms')
      .then((r) => setForms(r?.data?.forms || []))
      .catch((e) => setError(e?.response?.data?.detail || 'Erro a carregar formulários FB.'));
  }, []);

  const submit = async (dryRun) => {
    setError(''); setResult(null);
    if (!file) { setError('Selecione um ficheiro.'); return; }
    if (!formId) { setError('Escolha o formulário de origem.'); return; }

    const fd = new FormData();
    fd.append('file', file);
    fd.append('form_id', formId);
    fd.append('send_emails', sendEmails ? 'true' : 'false');
    fd.append('dry_run', dryRun ? 'true' : 'false');

    setLoading(true);
    let pollHandle = null;
    try {
      // 1) Inicia o job (resposta imediata com job_id) — ou resposta inline (versão antiga)
      const r = await api.post('/backoffice/leads/import-facebook', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 0,  // sem timeout do axios — depende do proxy
      });
      const data = r?.data || {};

      // Backwards compatible: se a resposta tem `results`, é a versão síncrona antiga
      // (ainda em produção pré-deploy). Mostra directamente.
      if (Array.isArray(data.results)) {
        setResult({ ...data, status: 'done' });
        return;
      }

      const jobId = data.job_id;
      if (!jobId) throw new Error('Resposta inválida do servidor (sem job_id nem results).');

      // 2) Polling do estado do job (a cada 2s, max ~10min)
      setResult({ ...data, status: 'running', processed: 0, results: [] });
      const start = Date.now();
      const POLL_MS = 2000;
      const MAX_MS = 10 * 60 * 1000;

      await new Promise((resolve, reject) => {
        const tick = async () => {
          try {
            const s = await api.get(`/backoffice/leads/import-facebook/status/${jobId}`);
            const sData = s?.data || {};
            setResult(sData);
            if (sData.status === 'done' || sData.status === 'error') { resolve(); return; }
            if (Date.now() - start > MAX_MS) { reject(new Error('Timeout — a importação demorou mais de 10 minutos.')); return; }
            pollHandle = setTimeout(tick, POLL_MS);
          } catch (e) { reject(e); }
        };
        tick();
      });
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Erro na importação.');
    } finally {
      if (pollHandle) clearTimeout(pollHandle);
      setLoading(false);
    }
  };

  const selectedForm = forms.find((f) => f.form_id === formId);

  return (
    <div className="space-y-6" data-testid="tab-fb-leads">
      <div className="bg-white rounded-xl border border-[#E5E7EB] p-5 shadow-sm">
        <div className="flex items-center gap-3 mb-2">
          <FacebookLogo size={24} weight="fill" className="text-[#1877F2]" />
          <h3 className="text-base font-semibold text-[#111827]">Importar Leads do Facebook</h3>
        </div>
        <p className="text-xs text-[#6B7280] mb-5">
          Use este formulário para recuperar leads quando o webhook automático estiver indisponível
          (App restringida, token expirado). Os ficheiros aceites são <strong>.xlsx</strong>, <strong>.csv</strong> ou <strong>.tsv</strong> com
          as colunas: <code className="font-mono text-[11px] bg-gray-100 px-1">created_time, full_name, email, phone_number, city</code>.
        </p>

        <div className="grid gap-4">
          {/* Formulário FB */}
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1.5">Formulário Facebook (Form ID)</label>
            <select value={formId} onChange={(e) => setFormId(e.target.value)} data-testid="fb-import-form-select"
              className="w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm">
              <option value="">— Escolha o formulário —</option>
              {forms.map((f) => (
                <option key={f.form_id} value={f.form_id}>
                  {f.nome_anuncio} (TipoLead {f.tipo_lead}, {f.pais_default})
                </option>
              ))}
            </select>
            {selectedForm && (
              <p className="text-[11px] text-[#6B7280] mt-1">
                Form ID: <span className="font-mono">{selectedForm.form_id}</span> · LandingPage: <span className="font-mono">{selectedForm.landing_page}</span>
              </p>
            )}
          </div>

          {/* Ficheiro */}
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1.5">Ficheiro de leads</label>
            <input type="file" data-testid="fb-import-file"
              accept=".xlsx,.xlsm,.csv,.tsv,.txt"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full text-sm" />
            {file && (
              <p className="text-[11px] text-[#6B7280] mt-1">
                {file.name} · {(file.size / 1024).toFixed(1)} KB
              </p>
            )}
          </div>

          {/* Email aos consultores */}
          <label className="inline-flex items-center gap-2 text-sm text-[#374151]">
            <input type="checkbox" checked={sendEmails}
              onChange={(e) => setSendEmails(e.target.checked)}
              data-testid="fb-import-send-emails" />
            Enviar email "Atribuição de uma Nova Lead" aos consultores atribuídos
          </label>

          {/* Acções */}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => submit(true)} disabled={loading}
              data-testid="fb-import-dry-run-btn"
              className="px-4 py-2 bg-white border-2 border-[#0F1829] text-[#0F1829] text-sm font-semibold rounded-md hover:bg-[#F3F4F6] disabled:opacity-50 inline-flex items-center gap-2">
              <Eye size={16} weight="bold" /> Pré-visualizar (dry-run)
            </button>
            <button type="button" onClick={() => submit(false)} disabled={loading}
              data-testid="fb-import-submit-btn"
              className="px-4 py-2 bg-[#0F1829] text-white text-sm font-semibold rounded-md hover:bg-[#001f6e] disabled:opacity-50 inline-flex items-center gap-2">
              {loading ? <SpinnerGap size={16} className="animate-spin" /> : <Upload size={16} weight="bold" />}
              Importar para a base de dados
            </button>
          </div>

          {error && (
            <div data-testid="fb-import-error" className="p-3 rounded-md bg-red-50 border border-red-200 text-[#DC2626] text-sm flex items-start gap-2">
              <Warning size={18} className="shrink-0 mt-0.5" /> <span>{error}</span>
            </div>
          )}
        </div>
      </div>

      {/* Resultado */}
      {result && (
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-5 shadow-sm" data-testid="fb-import-result">
          <div className="flex items-center gap-3 mb-3">
            {result.status === 'running' ? (
              <SpinnerGap size={22} className="animate-spin text-[#0F1829]" />
            ) : (
              <CheckCircle size={22} weight="fill" className="text-emerald-600" />
            )}
            <h3 className="text-base font-semibold text-[#111827]">
              {result.status === 'running' ? 'A importar...' :
               result.dry_run ? 'Pré-visualização' : 'Importação concluída'}
            </h3>
            {result.dry_run && (
              <span className="text-[10px] uppercase tracking-wide bg-amber-100 text-amber-800 px-2 py-0.5 rounded">DRY-RUN</span>
            )}
          </div>

          {/* Progress bar */}
          {result.status === 'running' && (
            <div className="mb-4" data-testid="fb-import-progress">
              <div className="flex items-center justify-between text-xs text-[#6B7280] mb-1">
                <span>Processadas {result.processed || 0} de {result.total}</span>
                <span className="tabular-nums">{result.total > 0 ? Math.round(100 * (result.processed || 0) / result.total) : 0}%</span>
              </div>
              <div className="w-full h-2 bg-[#F3F4F6] rounded-full overflow-hidden">
                <div className="h-full bg-[#0F1829] transition-all duration-300"
                  style={{ width: `${result.total > 0 ? 100 * (result.processed || 0) / result.total : 0}%` }} />
              </div>
            </div>
          )}

          <div className="grid grid-cols-4 gap-3 mb-4 text-sm">
            <Stat label="Total" value={result.total} />
            <Stat label={result.dry_run ? 'Validadas' : 'Criadas'} value={result.created} color="text-emerald-600" />
            <Stat label="Saltadas (já existem)" value={result.skipped} color="text-[#6B7280]" />
            <Stat label="Erros" value={result.errors} color="text-[#DC2626]" />
          </div>

          {result.form && (
            <p className="text-xs text-[#6B7280] mb-3">
              Formulário: <strong>{result.form.nome_anuncio}</strong> · TipoLead {result.form.tipo_lead} · Evento {result.form.evento}
            </p>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-[#F9FAFB] text-left">
                <tr>
                  <th className="px-2 py-1.5">Nome</th>
                  <th className="px-2 py-1.5">Email</th>
                  <th className="px-2 py-1.5">Telemóvel</th>
                  <th className="px-2 py-1.5">Cidade</th>
                  <th className="px-2 py-1.5">Resultado</th>
                </tr>
              </thead>
              <tbody>
                {(result.results || []).map((r) => (
                  <tr key={`${r.row.email}-${r.id_negocio || 'pending'}`} className="border-t border-[#F3F4F6]">
                    <td className="px-2 py-1.5">{r.row.full_name}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px]">{r.row.email}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px]">{r.row.phone_number}</td>
                    <td className="px-2 py-1.5">{r.row.city}</td>
                    <td className="px-2 py-1.5">
                      {r.ok ? (
                        r.dry_run ? (
                          <span className="text-amber-700">→ {r.preview?.tipoLead} ({r.preview?.pais})</span>
                        ) : (
                          <span className="text-emerald-700">
                            ✓ #{r.id_negocio} · cli#{r.id_cliente}{r.cliente_criado ? ' (novo)' : ''}{r.email_sent ? ' · email enfileirado' : ''}
                          </span>
                        )
                      ) : (
                        <span className="text-[#DC2626]" title={r.error}>✗ {r.error?.slice(0, 60)}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color = 'text-[#0F1829]' }) {
  return (
    <div className="bg-[#F9FAFB] rounded-md px-3 py-2">
      <p className="text-[10px] uppercase tracking-wide text-[#6B7280]">{label}</p>
      <p className={`text-2xl font-black tabular-nums ${color}`}>{value ?? 0}</p>
    </div>
  );
}
